import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ManualFindingStatusService } from '@core/services/manual-finding-status.service';
import { EFindingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import {
  EManualFindingStatus,
  ICreateManualFindingStatusPayload,
  IManualFindingStatus,
  IPendingManualFindingStatus,
} from '@interfaces/manual-finding-status.interface';
import { catchError, forkJoin, Observable, of, switchMap, tap } from 'rxjs';

// Inverse of DeliverableFindingsService.mapDeliverableTypeToFindingType - that map is exhaustive
// over EDeliverableType, so every EFindingType value here has exactly one source deliverableType
// (including topic, which only ever comes from EDeliverableType.talkingPoint).
const FINDING_TYPE_TO_DELIVERABLE_TYPE: Record<EFindingType, EDeliverableType> = {
  [EFindingType.title]: EDeliverableType.title,
  [EFindingType.gamePlay]: EDeliverableType.gamePlay,
  [EFindingType.chatBot]: EDeliverableType.chatBot,
  [EFindingType.chatbotMulti]: EDeliverableType.chatbotMulti,
  [EFindingType.topic]: EDeliverableType.talkingPoint,
  [EFindingType.talkingPointAi]: EDeliverableType.talkingPointAi,
  [EFindingType.visual]: EDeliverableType.visual,
  [EFindingType.productPlacement]: EDeliverableType.productPlacement,
  [EFindingType.showcase]: EDeliverableType.showcase,
  [EFindingType.textTracking]: EDeliverableType.textTracking,
  [EFindingType.audioTracking]: EDeliverableType.audioTracking,
  [EFindingType.caption]: EDeliverableType.caption,
  [EFindingType.hashtag]: EDeliverableType.hashTag,
  [EFindingType.mention]: EDeliverableType.mention,
  [EFindingType.message]: EDeliverableType.message,
  [EFindingType.description]: EDeliverableType.description,
  [EFindingType.content]: EDeliverableType.content,
  [EFindingType.custom]: EDeliverableType.custom,
};

@Injectable()
export class ManualFindingStatusStore {
  private readonly manualFindingStatusService = inject(ManualFindingStatusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventTrackingService = inject(EventTrackingService);

  // Loaded from server on init (only where status === 'removed')
  private readonly existingRemovedIds = signal<Set<string>>(new Set());
  // In-memory pending: kept for revert button visibility; added on delete, cleared on revert completion
  private readonly pendingRemovals = signal<Map<string, IPendingManualFindingStatus>>(new Map());
  // FindingIds whose create/update request has returned successfully
  private readonly confirmedRemovedIds = signal<Set<string>>(new Set());
  // findingId → server-assigned record ID (needed for PUT revert)
  private readonly serverRecordIds = signal<Map<string, string>>(new Map());
  // findingId → full server payload (needed to send full object on PUT revert)
  private readonly serverPayloads = signal<Map<string, ICreateManualFindingStatusPayload>>(new Map());
  // FindingIds with a create/update request currently in-flight
  private readonly inFlightDeletionFindingIds = signal<Set<string>>(new Set());
  // VisualizerIds with PUT revert currently in-flight
  private readonly inFlightRevertVisualizerIds = signal<Set<string>>(new Set());

  // Cached from loadForCampaign — used to narrow all getAll requests to this social content
  private socialContentId: string | null = null;

  public readonly hasPending = computed(() => this.pendingRemovals().size > 0);

  // Used for highlighting and row filtering: server-side confirmed only (not in-flight)
  public readonly allRemovedIds = computed<Set<string>>(() => {
    return new Set([...this.existingRemovedIds(), ...this.confirmedRemovedIds()]);
  });

  // Only findings persisted before dialog opened — used by buildFromKeywords to exclude rows
  public readonly serverRemovedIds = computed<Set<string>>(() => {
    return new Set(this.existingRemovedIds());
  });

  public loadForCampaign(campaignId: string, socialContentId?: string | null, campaignExportId?: string | null): void {
    this.socialContentId = socialContentId ?? null;

    const params = { campaignId, socialContentId: socialContentId ?? undefined };
    const request$ = campaignExportId
      ? this.manualFindingStatusService.getAllExport(campaignExportId, params)
      : this.manualFindingStatusService.getAll(params);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response?.data) {
          const removedRecords = response.data.filter((r) => r.status === EManualFindingStatus.removed);

          this.existingRemovedIds.set(new Set(removedRecords.map((r) => r.findingId)));

          // Pre-populate record IDs and payloads for ALL records (including status=null).
          // null-status records need a PUT (not POST) when deleted again.
          const recordIds = new Map<string, string>();
          const payloads = new Map<string, ICreateManualFindingStatusPayload>();

          for (const r of response.data) {
            recordIds.set(r.findingId, r.id);
            payloads.set(r.findingId, {
              campaignId: r.campaignId,
              creatorId: r.creatorId,
              deliverableId: r.deliverableId,
              findingId: r.findingId,
              findingType: r.findingType,
              parentId: r.parentId,
              socialContentId: r.socialContentId,
            });
          }

          this.serverRecordIds.set(recordIds);
          this.serverPayloads.set(payloads);
        }
      },
    });
  }

  public markForRemoval(pending: IPendingManualFindingStatus): void {
    if (this.pendingRemovals().has(pending.findingId) || this.allRemovedIds().has(pending.findingId)) {
      return;
    }

    this.addToPendingRemovals(pending);
    this.addToInFlightDeletion(pending.findingId);

    const payload = this.buildPayload(pending);
    const existingRecordId = this.serverRecordIds().get(pending.findingId);
    const request$ = existingRecordId
      ? this.manualFindingStatusService.update(existingRecordId, { ...payload, status: EManualFindingStatus.removed })
      : this.manualFindingStatusService.create(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.removeFromInFlightDeletion(pending.findingId);
        this.addToConfirmedRemovals(pending.findingId);
        this.cacheServerPayload(pending.findingId, payload);

        if (response?.data?.id) {
          this.cacheServerRecordId(pending.findingId, response.data.id);
        }

        this.eventTrackingService.track(EEventType.campaignAnalyticsManualFindingStatusSet, {
          campaignId: payload.campaignId,
          deliverableId: payload.deliverableId,
          deliverableType: FINDING_TYPE_TO_DELIVERABLE_TYPE[payload.findingType],
          newValue: EManualFindingStatus.removed,
          socialContentId: payload.socialContentId,
        });
      },

      error: () => {
        this.removeFromInFlightDeletion(pending.findingId);
        this.removeFromPendingRemovals(pending.findingId);
      },
    });
  }

  public revertVisualizerDeletions(visualizerId: string): void {
    if (this.inFlightRevertVisualizerIds().has(visualizerId)) return;

    const toRevert = Array.from(this.pendingRemovals().values()).filter(
      (p) => p.visualizerId === visualizerId && this.confirmedRemovedIds().has(p.findingId),
    );

    if (toRevert.length === 0) return;

    const updatedReverts = new Set(this.inFlightRevertVisualizerIds());

    updatedReverts.add(visualizerId);
    this.inFlightRevertVisualizerIds.set(updatedReverts);

    // Single batch GET if any record ID is missing — avoids N+1 GETs in buildRevertRequest.
    const needsLookup = toRevert.some((p) => !this.serverRecordIds().has(p.findingId));
    const prefetch$ = needsLookup
      ? this.manualFindingStatusService
          .getAll({ campaignId: toRevert[0].campaignId, socialContentId: this.socialContentId ?? undefined })
          .pipe(
            tap((response) => {
              for (const r of response?.data ?? []) {
                this.cacheServerRecordId(r.findingId, r.id);
                this.cacheServerPayload(r.findingId, this.buildPayload(r));
              }
            }),
            catchError(() => of(null)),
          )
      : of(null);

    prefetch$
      .pipe(
        switchMap(() => forkJoin(toRevert.map((p) => this.buildRevertRequest(p)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => {
          const reverts = new Set(this.inFlightRevertVisualizerIds());

          reverts.delete(visualizerId);
          this.inFlightRevertVisualizerIds.set(reverts);
        },
        next: (results) => {
          const reverts = new Set(this.inFlightRevertVisualizerIds());

          reverts.delete(visualizerId);
          this.inFlightRevertVisualizerIds.set(reverts);

          const updatedPending = new Map(this.pendingRemovals());
          const confirmed = new Set(this.confirmedRemovedIds());

          for (let i = 0; i < results.length; i++) {
            if (results[i] !== null) {
              updatedPending.delete(toRevert[i].findingId);
              confirmed.delete(toRevert[i].findingId);

              this.eventTrackingService.track(EEventType.campaignAnalyticsManualFindingStatusSet, {
                campaignId: toRevert[i].campaignId,
                deliverableId: toRevert[i].deliverableId,
                deliverableType: FINDING_TYPE_TO_DELIVERABLE_TYPE[toRevert[i].findingType],
                previousValue: EManualFindingStatus.removed,
                socialContentId: toRevert[i].socialContentId,
              });
            }
          }

          this.pendingRemovals.set(updatedPending);
          this.confirmedRemovedIds.set(confirmed);
        },
      });
  }

  private buildPayload(pending: ICreateManualFindingStatusPayload): ICreateManualFindingStatusPayload {
    return {
      campaignId: pending.campaignId,
      creatorId: pending.creatorId,
      deliverableId: pending.deliverableId,
      findingId: pending.findingId,
      findingType: pending.findingType,
      parentId: pending.parentId,
      socialContentId: pending.socialContentId,
    };
  }

  private addToPendingRemovals(pending: IPendingManualFindingStatus): void {
    const map = new Map(this.pendingRemovals());

    map.set(pending.findingId, pending);
    this.pendingRemovals.set(map);
  }

  private removeFromPendingRemovals(findingId: string): void {
    const map = new Map(this.pendingRemovals());

    map.delete(findingId);
    this.pendingRemovals.set(map);
  }

  private addToInFlightDeletion(findingId: string): void {
    const set = new Set(this.inFlightDeletionFindingIds());

    set.add(findingId);
    this.inFlightDeletionFindingIds.set(set);
  }

  private removeFromInFlightDeletion(findingId: string): void {
    const set = new Set(this.inFlightDeletionFindingIds());

    set.delete(findingId);
    this.inFlightDeletionFindingIds.set(set);
  }

  private addToConfirmedRemovals(findingId: string): void {
    const set = new Set(this.confirmedRemovedIds());

    set.add(findingId);
    this.confirmedRemovedIds.set(set);
  }

  private cacheServerPayload(findingId: string, payload: ICreateManualFindingStatusPayload): void {
    const map = new Map(this.serverPayloads());

    map.set(findingId, payload);
    this.serverPayloads.set(map);
  }

  private cacheServerRecordId(findingId: string, recordId: string): void {
    const map = new Map(this.serverRecordIds());

    map.set(findingId, recordId);
    this.serverRecordIds.set(map);
  }

  private buildRevertRequest(p: IPendingManualFindingStatus): Observable<IApiResponse<IManualFindingStatus> | null> {
    const cachedRecordId = this.serverRecordIds().get(p.findingId);
    const cachedPayload = this.serverPayloads().get(p.findingId) ?? this.buildPayload(p);

    if (!cachedRecordId) return of(null);

    return this.manualFindingStatusService
      .update(cachedRecordId, { ...cachedPayload, status: null })
      .pipe(catchError(() => of(null)));
  }

  public getPendingForVisualizer(visualizerId: string): IPendingManualFindingStatus[] {
    return Array.from(this.pendingRemovals().values()).filter((p) => p.visualizerId === visualizerId);
  }

  /** True while a create/update request is in-flight for this finding. Used to show spinner in delete menu. */
  public isFindingInFlight(findingId: string): boolean {
    return this.inFlightDeletionFindingIds().has(findingId);
  }

  /**
   * True if any action is in-flight for this visualizer row:
   * - A PUT revert is in-flight, OR
   * - Any create/update request for a finding in this row is in-flight.
   * Used to show spinner on the revert button.
   */
  public isVisualizerActing(visualizerId: string): boolean {
    if (this.inFlightRevertVisualizerIds().has(visualizerId)) {
      return true;
    }

    return this.getPendingForVisualizer(visualizerId).some((p) => this.inFlightDeletionFindingIds().has(p.findingId));
  }

  /** True if this finding already has a pending or confirmed removal (prevents double-deletion). */
  public isHandledFinding(findingId: string): boolean {
    return this.pendingRemovals().has(findingId) || this.allRemovedIds().has(findingId);
  }
}
